import { ComponentType } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { twc, TwcComponentProps } from 'react-twc';

import { Kbd } from '~/modules/elements/akui/kbd';
import Box from '~/modules/elements/akui/primitives/box';
import Typography from '~/modules/elements/akui/primitives/typography';
import { supportsEscAsBack } from '~/modules/hooks/use-keyboard';
import {
  KeyboardHelpVisibilitySetting,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';

import { RegularHelpEntry } from './context';

interface Props {
  help: RegularHelpEntry;
  /** See `HelpEntry.presentation` - the corner panel unless a screen asks for the bottom strip. */
  presentation?: 'panel' | 'strip';
}

export default function KeyboardHelpView({ help, presentation = 'panel' }: Props) {
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const [isVisible, setIsVisible] = useSettingValue(KeyboardHelpVisibilitySetting);

  useHotkeys('shift+h', () => setIsVisible(!isVisible), undefined, [isVisible]);

  // Only keys this view actually has a renderer for. `HelpEntry` also carries remote-mic-only
  // metadata (title, icon, mirrored controls) which the provider strips out — but this view sits at
  // the app root, so an unrecognised key reaching the lookup below would throw and take the whole UI
  // down with it. Skipping unknown keys keeps that failure mode impossible.
  const helps = Object.entries(help ?? {}).filter(([type, value]) => value !== undefined && type in KeyhelpComponent);
  const hasHelp = Object.values(help ?? {}).some((value) => value !== undefined);

  if (mobilePhoneMode) {
    return null;
  }

  if (presentation === 'strip') {
    // `pointer-events-none`: the strip is purely informational but spans the full width, so
    // without this it would swallow clicks on whatever sits under its band.
    return (
      <Strip data-test="hint-strip" data-visible={hasHelp && isVisible}>
        {isVisible &&
          helps.map(([type, label]) => {
            const { view: Component, defaultLabel } = KeyhelpComponent[type as keyof RegularHelpEntry];

            return (
              <span key={type} className="flex items-center gap-2">
                <span className="[&_br]:hidden">
                  <Component />
                </span>
                {label ?? defaultLabel}
              </span>
            );
          })}
      </Strip>
    );
  }

  return (
    <>
      <Container
        data-test="help-container"
        onClick={() => setIsVisible(!isVisible)}
        data-visible={hasHelp && isVisible}>
        {isVisible && (
          <>
            <UseKeyboardIndicator className="UseKeyboardIndicator">
              Use indicated keys on your keyboard
            </UseKeyboardIndicator>
            {helps.map(([type, label]) => {
              const { view: Component, defaultLabel } = KeyhelpComponent[type as keyof RegularHelpEntry];
              return (
                <Section key={type}>
                  <SectionHelp>{label ?? defaultLabel}</SectionHelp>
                  <SectionKeys>
                    <Component />
                  </SectionKeys>
                </Section>
              );
            })}
            <Section>
              <SectionHelp>Show/hide this help</SectionHelp>
              <SectionKeys>{ShiftLetter('h')()}</SectionKeys>
            </Section>
          </>
        )}
      </Container>
    </>
  );
}

const HorizontalVerticalBase = ({ vertical = false, horizontal = false }) => (
  <>
    <Kbd disabled={!vertical}>↑</Kbd>
    <br />
    <Kbd disabled={!horizontal}>←</Kbd>
    <Kbd disabled={!vertical}>↓</Kbd>
    <Kbd disabled={!horizontal}>→</Kbd>
  </>
);

const Horizontal = () => <HorizontalVerticalBase horizontal />;
const Vertical = () => <HorizontalVerticalBase vertical />;
const HorizontalVertical = () => <HorizontalVerticalBase vertical horizontal />;
const Accept = () => <Kbd>Enter ⏎</Kbd>;
const Back = () => (supportsEscAsBack ? <Kbd>Escape</Kbd> : <Kbd>Backspace ⌫</Kbd>);
const ShiftLetter = (letter: string) => () => (
  <>
    <Kbd>Shift</Kbd> + <Kbd>{letter.toUpperCase()}</Kbd>
  </>
);
const Alphanumeric = () => (
  <>
    <Kbd>start typing</Kbd>
  </>
);

const KeyhelpComponent: Record<keyof RegularHelpEntry, { view: ComponentType; defaultLabel: string }> = {
  'horizontal-vertical': { view: HorizontalVertical, defaultLabel: 'Navigate' },
  horizontal: { view: Horizontal, defaultLabel: 'Navigate' },
  vertical: { view: Vertical, defaultLabel: 'Navigate' },
  accept: { view: Accept, defaultLabel: 'Select' },
  back: { view: Back, defaultLabel: 'Go back' },
  shiftR: { view: ShiftLetter('r'), defaultLabel: 'Pick random' },
  alphanumeric: { view: Alphanumeric, defaultLabel: 'Search' },
};

const Section = twc.div`flex items-center gap-4`;

const SectionKeys = twc.div`flex-2 flex-nowrap text-center font-bold text-white`;

const SectionHelp = twc(Typography)`text-md flex-3 text-right`;

const UseKeyboardIndicator = twc(
  Typography,
)`text-md invisible absolute inset-0 flex items-center justify-center bg-black/75 p-8 text-white opacity-0 duration-300 hover:visible hover:opacity-100`;

/**
 * The arrow keys render as a two-row cluster in the panel; on one line the `<br>` between them is
 * suppressed so ↑ ← ↓ → sit in a row.
 */
const Strip = twc.div((props: TwcComponentProps<'div'> & { 'data-visible': boolean }) => [
  `typography pointer-events-none fixed inset-x-0 bottom-0 z-1000 h-[3.5rem] items-center justify-center gap-8 bg-black/60 text-center text-md font-bold text-white/70 [view-transition-name:hint-strip] [&_svg]:fill-white`,
  props['data-visible'] ? 'mobile:hidden flex' : 'hidden',
]);

const Container = twc(Box)((props: TwcComponentProps<'div'> & { 'data-visible': boolean }) => [
  `fixed top-[5rem] right-0 z-1000 w-[28rem] scale-75 cursor-pointer items-stretch gap-4 p-2 [view-transition-name:help-view] hover:[&_.UseKeyboardIndicator]:visible hover:[&_.UseKeyboardIndicator]:opacity-100 [&_svg]:fill-white`,
  props['data-visible'] ? 'mobile:hidden flex' : 'hidden',
]);
